import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getStats, getLogs, getTopics, calculateCost, StatsResponse, PagedLogsResponse, TopicCount } from '../services/api';
import { useTheme } from '../ThemeContext';

type Preset = '1h' | '24h' | '7d' | '30d' | 'custom';

const presets: { key: Preset; label: string; ms: number }[] = [
  { key: '1h', label: '1 hodina', ms: 3600_000 },
  { key: '24h', label: '24 hodín', ms: 86400_000 },
  { key: '7d', label: '7 dní', ms: 7 * 86400_000 },
  { key: '30d', label: '30 dní', ms: 30 * 86400_000 },
];

function formatTokens(n: number): string {
  return n < 1000 ? n.toString() : (n / 1000).toFixed(2) + 'k';
}

function formatCost(inputTokens: number, outputTokens: number): string {
  const usd = calculateCost(inputTokens, outputTokens);
  if (usd < 0.01) return `$${usd.toFixed(4)}`;
  return `$${usd.toFixed(2)}`;
}

function toLocalDatetime(date: Date): string {
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { mode, setMode } = useTheme();

  const [preset, setPreset] = useState<Preset>('24h');
  const [customFrom, setCustomFrom] = useState(() => toLocalDatetime(new Date(Date.now() - 86400_000)));
  const [customTo, setCustomTo] = useState(() => toLocalDatetime(new Date()));
  const [stats, setStats] = useState<StatsResponse | null>(null);
  const [logs, setLogs] = useState<PagedLogsResponse | null>(null);
  const [topics, setTopics] = useState<TopicCount[]>([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const getDateRange = useCallback((): { from: string; to: string } => {
    if (preset === 'custom') {
      return { from: new Date(customFrom).toISOString(), to: new Date(customTo).toISOString() };
    }
    const p = presets.find((p) => p.key === preset)!;
    return { from: new Date(Date.now() - p.ms).toISOString(), to: new Date().toISOString() };
  }, [preset, customFrom, customTo]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { from, to } = getDateRange();
      const [statsData, logsData, topicsData] = await Promise.all([
        getStats(from, to),
        getLogs(from, to, page, 20),
        getTopics(from, to),
      ]);
      setStats(statsData);
      setLogs(logsData);
      setTopics(topicsData);
    } catch {
      setStats(null);
      setLogs(null);
      setTopics([]);
    } finally {
      setLoading(false);
    }
  }, [getDateRange, page]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    setPage(1);
  }, [preset, customFrom, customTo]);

  const totalPages = logs ? Math.ceil(logs.totalCount / logs.pageSize) : 0;

  const themeNext = mode === 'light' ? 'dark' : mode === 'dark' ? 'auto' : 'light';
  const themeIcon = mode === 'light' ? '☀️' : mode === 'dark' ? '🌙' : '⚙️';

  const sectionStyle: React.CSSProperties = { background: 'var(--bg-info)', borderRadius: 10, padding: '10px 20px', marginBottom: 8, borderBottom: '1px solid var(--border-color)' };
  const statBoxStyle: React.CSSProperties = { textAlign: 'center', padding: '12px 0' };
  const statValue: React.CSSProperties = { fontSize: 24, fontWeight: 'bold', color: 'var(--text-primary)' };
  const statLabel: React.CSSProperties = { fontSize: 11, color: 'var(--text-muted)', marginTop: 2 };

  // Compute quality & language counts from topics/logs
  const qualityCounts = logs ? logs.items.reduce((acc, l) => {
    const q = l.answerQuality || 'no_data';
    acc[q] = (acc[q] || 0) + 1;
    return acc;
  }, {} as Record<string, number>) : {};

  const languageCounts = logs ? logs.items.reduce((acc, l) => {
    const lang = l.language || '?';
    acc[lang] = (acc[lang] || 0) + 1;
    return acc;
  }, {} as Record<string, number>) : {};

  const avgResponseTime = logs && logs.items.length > 0
    ? Math.round(logs.items.filter(l => l.responseTimeMs != null).reduce((s, l) => s + (l.responseTimeMs || 0), 0) / (logs.items.filter(l => l.responseTimeMs != null).length || 1))
    : null;

  const maxTopicCount = topics.length > 0 ? topics[0].count : 1;

  const thStyle: React.CSSProperties = { padding: '6px 8px', textAlign: 'left', fontSize: 11, color: 'var(--text-muted)', borderBottom: '1px solid var(--border-color)', whiteSpace: 'nowrap' };
  const tdStyle: React.CSSProperties = { padding: '6px 8px', fontSize: 12, borderBottom: '1px solid var(--border-color)', color: 'var(--text-primary)', whiteSpace: 'nowrap' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', maxWidth: 1100, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ padding: '16px 20px', background: 'var(--header-bg)', color: '#fff', borderRadius: '12px 12px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <img src={`${process.env.PUBLIC_URL}/favicon.svg`} alt="" style={{ width: 32, height: 32, cursor: 'pointer' }} onClick={() => navigate('/')} title="Späť na chat" />
          <div>
            <h1 style={{ margin: 0, fontSize: 18 }}>Dashboard</h1>
            <div style={{ fontSize: 12, opacity: 0.8 }}>Globálne štatistiky a logy</div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button onClick={() => setMode(themeNext)} title={themeNext} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', width: 32, height: 32, borderRadius: '50%', cursor: 'pointer', fontSize: 16, lineHeight: '32px' }}>{themeIcon}</button>
          <button onClick={() => navigate('/stats')} title="Štatistiky relácie" style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff', width: 32, height: 32, borderRadius: '50%', cursor: 'pointer', fontSize: 16, lineHeight: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 19l-7-7 7-7" /></svg>
          </button>
        </div>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflow: 'auto', padding: 16, background: 'var(--bg-chat)' }}>

        {/* Time filter */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
          {presets.map((p) => (
            <button key={p.key} onClick={() => setPreset(p.key)} style={{
              padding: '5px 12px', borderRadius: 6, border: '1px solid var(--border-color)',
              background: preset === p.key ? 'var(--accent)' : 'transparent',
              color: preset === p.key ? '#fff' : 'var(--text-primary)', cursor: 'pointer', fontSize: 12,
            }}>{p.label}</button>
          ))}
          <button onClick={() => setPreset('custom')} style={{
            padding: '5px 12px', borderRadius: 6, border: '1px solid var(--border-color)',
            background: preset === 'custom' ? 'var(--accent)' : 'transparent',
            color: preset === 'custom' ? '#fff' : 'var(--text-primary)', cursor: 'pointer', fontSize: 12,
          }}>Vlastné</button>
        </div>

        {preset === 'custom' && (
          <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <label style={{ fontSize: 14 }}>
              Od: <input type="datetime-local" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)}
                style={{ fontSize: 14, padding: '8px 12px', borderRadius: 6, border: '1px solid var(--border-color)', background: 'var(--bg-chat)', color: 'var(--text-primary)' }} />
            </label>
            <label style={{ fontSize: 14 }}>
              Do: <input type="datetime-local" value={customTo} onChange={(e) => setCustomTo(e.target.value)}
                style={{ fontSize: 14, padding: '8px 12px', borderRadius: 6, border: '1px solid var(--border-color)', background: 'var(--bg-chat)', color: 'var(--text-primary)' }} />
            </label>
          </div>
        )}

        {/* Global stat cards */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: 10, color: 'var(--text-muted)' }}>Načítavam...</div>
        ) : stats && (
          <>
            <div style={sectionStyle}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
                <div style={statBoxStyle}>
                  <div style={statValue}>{stats.queryCount}</div>
                  <div style={statLabel}>otázok</div>
                </div>
                <div style={statBoxStyle}>
                  <div style={statValue}>{formatTokens(stats.totalInputTokens + stats.totalOutputTokens)}</div>
                  <div style={statLabel}>celkové tokeny</div>
                </div>
                <div style={statBoxStyle}>
                  <div style={statValue}>{formatCost(stats.totalInputTokens, stats.totalOutputTokens)}</div>
                  <div style={statLabel}>celková cena</div>
                </div>
                <div style={statBoxStyle}>
                  <div style={statValue}>{avgResponseTime != null ? `${avgResponseTime}ms` : '—'}</div>
                  <div style={statLabel}>Ø čas odpovede</div>
                </div>
              </div>
            </div>

            <div className="dashboard-mid-row">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div style={sectionStyle}>
                  <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: 'var(--text-primary)' }}>Kvalita</div>
                  <div style={{ fontSize: 12, color: 'var(--text-primary)' }}>
                    {Object.entries(qualityCounts).map(([k, v]) => (
                      <div key={k} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                        <span>{k}</span><span style={{ fontWeight: 600 }}>{v}</span>
                      </div>
                    ))}
                    {Object.keys(qualityCounts).length === 0 && <span style={{ color: 'var(--text-muted)' }}>—</span>}
                  </div>
                </div>
                <div style={sectionStyle}>
                  <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: 'var(--text-primary)' }}>Jazyky</div>
                  <div style={{ fontSize: 12, color: 'var(--text-primary)' }}>
                    {Object.entries(languageCounts).map(([k, v]) => (
                      <div key={k} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                        <span>{k}</span><span style={{ fontWeight: 600 }}>{v}</span>
                      </div>
                    ))}
                    {Object.keys(languageCounts).length === 0 && <span style={{ color: 'var(--text-muted)' }}>—</span>}
                  </div>
                </div>
              </div>
              <div style={{ ...sectionStyle, marginBottom: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: 'var(--text-primary)' }}>Top oblasti záujmu</div>
                {topics.slice(0, 5).map((t) => (
                  <div key={t.category} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <div style={{ width: 110, fontSize: 13, textAlign: 'right', flexShrink: 0, color: 'var(--text-primary)' }}>{t.category}</div>
                    <div style={{ flex: 1, background: 'var(--border-color)', borderRadius: 4, height: 20, overflow: 'hidden' }}>
                      <div style={{ width: `${(t.count / maxTopicCount) * 100}%`, height: '100%', background: 'var(--accent)', borderRadius: 4, transition: 'width 0.3s' }} />
                    </div>
                    <div style={{ width: 35, fontSize: 13, color: 'var(--text-muted)' }}>{t.count}</div>
                  </div>
                ))}
                {topics.length === 0 && <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>—</span>}
              </div>
            </div>
          </>
        )}

        {/* Log table */}
        {logs && logs.items.length > 0 && (
          <div style={{ ...sectionStyle, overflowX: 'auto' }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: 'var(--text-primary)' }}>Log ({logs.totalCount} záznamov)</div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr>
                  <th style={thStyle}>Dátum</th>
                  <th style={{ ...thStyle, whiteSpace: 'normal', minWidth: 180 }}>Otázka</th>
                  <th style={thStyle}>Kategória</th>
                  <th style={thStyle}>Kvalita</th>
                  <th style={thStyle}>Jazyk</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Skóre</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>IN</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>OUT</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Čas</th>
                </tr>
              </thead>
              <tbody>
                {logs.items.map((l) => (
                  <tr key={l.id}>
                    <td style={tdStyle}>{new Date(l.createdAt).toLocaleString('sk-SK', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</td>
                    <td style={{ ...tdStyle, whiteSpace: 'normal', maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis' }}>{l.question}</td>
                    <td style={tdStyle}>{l.category || '—'}</td>
                    <td style={tdStyle}>{l.answerQuality || '—'}</td>
                    <td style={tdStyle}>{l.language || '—'}</td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>{l.searchScore != null ? l.searchScore.toFixed(2) : '—'}</td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>{formatTokens(l.inputTokens)}</td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>{formatTokens(l.outputTokens)}</td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>{l.responseTimeMs != null ? `${l.responseTimeMs}ms` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, marginTop: 12 }}>
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1} style={{
                padding: '4px 12px', borderRadius: 6, border: '1px solid var(--border-color)',
                background: page <= 1 ? 'transparent' : 'var(--accent)', color: page <= 1 ? 'var(--text-muted)' : '#fff',
                cursor: page <= 1 ? 'default' : 'pointer', fontSize: 12,
              }}>Predošlá</button>
              <span style={{ fontSize: 12, color: 'var(--text-primary)' }}>Strana {page} z {totalPages}</span>
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages} style={{
                padding: '4px 12px', borderRadius: 6, border: '1px solid var(--border-color)',
                background: page >= totalPages ? 'transparent' : 'var(--accent)', color: page >= totalPages ? 'var(--text-muted)' : '#fff',
                cursor: page >= totalPages ? 'default' : 'pointer', fontSize: 12,
              }}>Ďalšia</button>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div style={{
        padding: '10px 20px', background: 'var(--bg-footer)', color: 'var(--text-muted)',
        fontSize: 11, textAlign: 'center', borderTop: '1px solid var(--border-color)',
      }}>
        <span>🧪 Experiment #463.2026.4</span>
        <span style={{ margin: '0 4px' }}>—</span>
        <span>Built in{' '}<a href="https://lab463.com" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)', textDecoration: 'none' }}>lab463</a></span>
      </div>
    </div>
  );
};
